import React, { useEffect, useMemo, useLayoutEffect, useRef } from 'react';
import find from 'lodash/find';
import { useDispatch, useSelector } from 'react-redux';
import { challengeSelectors, challengeActions } from 'redux/challenge';
import PropTypes from 'prop-types';
import FormField from 'components/FinalForm/Fields/FormField';
import { useFieldArray } from 'react-final-form-arrays';
import InputAdornment from '@mui/material/InputAdornment';
import Grid from 'components/Grid';
import { useTranslation } from 'react-i18next';
import { getFieldLabelTranslation, getUnitTranslation } from 'services/challengeService';
import { fieldValidator, isGTE } from 'validators';
import { useField, useForm, useFormState } from 'react-final-form';
import useConfirm from 'components/Confirmation/useConfirm';

const getValidator = (name, rules, value) => {
  if (!rules) {
    return undefined;
  }

  const min = rules.match(/(\||^)min:(\d+)(\||$)/);

  return fieldValidator(name, min && value ? [isGTE(min[2])] : []);
};

const fallbackCategoryField = {};

const CategoryField = ({ name, typeId, categoryName }) => {
  const { t } = useTranslation(['common', 'component']);

  const type = useSelector(challengeSelectors.getActivityCategoryFieldTypes)[typeId] || fallbackCategoryField;

  const { input: { value }, meta: { error } } = useField(`${name}.value`);

  const props = {
    name: `${name}.value`,
    label: getFieldLabelTranslation(t, type.name, categoryName),
    helperText: type.hasActiveChallenge && !error ? t('component:activityForm.requiredValue') : error,
    InputProps: {
      endAdornment: type.unit ? (
        <InputAdornment>{getUnitTranslation(t, +value, type.unit, categoryName)}</InputAdornment>
      ) : undefined,
    },
    inputProps: type.input || {},
    validate: value ? getValidator(type.name, type.rules, value) : null,
  };

  return <FormField margin="dense" {...props} />;
};

CategoryField.propTypes = {
  name: PropTypes.string.isRequired,
  categoryName: PropTypes.string,
  typeId: PropTypes.number,
};

const ActivityCategorySpecificFields = ({ name, categoryId }) => {
  const dispatch = useDispatch();

  const { t } = useTranslation(['component']);

  const form = useForm();

  const { values } = useFormState({ subscription: { values: true } });

  const { fields } = useFieldArray(name);

  const categories = useSelector(challengeSelectors.getActivityCategories);

  const category = useMemo(() => categories.find(({ id }) => +id === +categoryId), [categories, categoryId]);

  const typeSelector = useSelector(challengeSelectors.getActivityCategoryFieldTypes);

  const confirm = useConfirm();

  const skipConfirmation = useRef(false);

  const askForConfirmation = () => {
    confirm({
      title: t('activityForm.emptyValuesConfirmationTitle'),
      description: t('activityForm.emptyValuesConfirmationDescription'),
    })
      .then(() => {
        skipConfirmation.current = true; // Skip future confirmations until the form is rebuilt
        form.submit(); // If user approved then submit again
      })
      .catch(() => {
        skipConfirmation.current = false;
      });
  };
  useField('activity', {
    beforeSubmit: () => {
      const shouldBlockSubmit = values.activity.values.some((value) => {
        const type = typeSelector[value.typeId] || fallbackCategoryField;
        if (!value.value && type.hasActiveChallenge && !skipConfirmation.current) {
          askForConfirmation();
          return true; // stop iteration
        }

        return false;
      });

      return !shouldBlockSubmit;
    },
  });

  useEffect(() => {
    dispatch(challengeActions.getActivityCategories());
  }, [dispatch]);

  useLayoutEffect(() => {
    return () => form.change(fields.name, null);
  }, [form, fields.name]);

  useLayoutEffect(() => {
    if (!category) {
      return;
    }

    const newFields = (category.types || []).map(typeId => ({
      typeId,
      value: (find(fields.value, { typeId }) || {}).value,
    }));

    form.change(fields.name, newFields);
  }, [category]); // eslint-disable-line

  if (!fields.length) {
    return null;
  }

  return (
    <Grid container spacing={2}>
      {fields.map((fieldName, idx) => (
        <Grid key={fieldName} item xs={6}>
          <CategoryField name={fieldName} typeId={fields.value[idx].typeId} categoryName={category?.name} />
        </Grid>
      ))}
    </Grid>
  );
};

ActivityCategorySpecificFields.propTypes = {
  name: PropTypes.string,
  categoryId: PropTypes.any,
};

export default ActivityCategorySpecificFields;
